import { apiClient } from "@/services/api"
import { defineStore } from "pinia"
import { hasMoreRows } from "@/services/listAnswer"

let lastPage = -1

export const ADDITIONAL_EFFORT_FORM_NAMES = {
  GENERAL: "general",
  FILES: "files",
  ACTIVITY: "activity",
}

const _blankAdditionalEffort = () => {
  return {
    id: "",
    name: "",
    unit: null,
    legacyUnit: null,
    purchasePrices: [],
    note: null,
    files: null,
    createdAt: null,
    createdBy: null,
    lastModifiedAt: null,
    lastModifiedBy: null,
  }
}

export const useAdditionalEffortStore = defineStore("additionalEffort", {
  state: () => ({
    additionalEfforts: [],
    additionalEffort: _blankAdditionalEffort(),
  }),
  getters: {
    getAdditionalEfforts: state => state.additionalEfforts,
    getAdditionalEffort: state => state.additionalEffort,
  },
  actions: {
    setAdditionalEfforts(additionalEfforts) {
      this.additionalEfforts = [...additionalEfforts]
    },
    addAdditionalEfforts(additionalEfforts) {
      additionalEfforts.forEach(additionalEffort => this.additionalEfforts.push(additionalEffort))
    },
    clearAdditionalEfforts() {
      this.additionalEfforts = []
    },
    setAdditionalEffort(additionalEffort) {
      this.additionalEffort = additionalEffort
    },
    clearAdditionalEffort() {
      this.additionalEffort = _blankAdditionalEffort()
    },
    updateAdditionalEffort(additionalEffort) {
      if (additionalEffort.prices && additionalEffort.prices.length <= 0) {
        additionalEffort.prices = null
      }
      return apiClient
        .put(`/additionaleffort/${additionalEffort.id}`, additionalEffort, {
          headers: { tenant: true, authorization: true },
        })
        .then(res => {
          if (res.status === 200) {
            this.setAdditionalEffort(res.data)
          }
          return res
        })
    },
    createAdditionalEffort(additionalEffort, files = []) {
      if (additionalEffort.prices && additionalEffort.prices.length <= 0) {
        additionalEffort.prices = null
      }
      if (files && files.length > 0) {
        const formData = new FormData()
        formData.append('additionalEffort', new Blob([JSON.stringify(additionalEffort)], { type: 'application/json' }))
        files.forEach(file => formData.append('files', file))
        return apiClient.post('/additionaleffort/with-files', formData, { headers: { tenant: true, authorization: true } })
          .then(res => {
            if (res.status === 200) this.setAdditionalEffort(res.data)
            return res
          })
      }
      return apiClient
        .post("/additionaleffort", additionalEffort, { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setAdditionalEffort(res.data)
          }
          return res
        })
    },
    loadAdditionalEffort(id) {
      return apiClient.get(`/additionaleffort/${id}`, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 200) {
          if (res.data.purchasePrices == null) {
            res.data.purchasePrices = []
          }
          this.setAdditionalEffort(res.data)
        }
        return res
      })
    },
    loadAdditionalEfforts({ page, search, sortBy, sortDesc, selected, itemsPerPage, archive }) {
      return apiClient
        .get("/additionaleffort", {
          params: {
            // Unset for the records in use; `ARCHIVED` for the overview's archive (docs/archiving.md).
            archive,
            page: page,
            search: search,
            sortBy: sortBy,
            sortDesc: sortDesc,
            selected: selected,
            itemsPerPage: itemsPerPage,
          },
          headers: { tenant: true, authorization: true },
        })
        .then(res => {
          if (res.status === 200) {
            if (page === 0) {
              this.setAdditionalEfforts(res.data.content)
            } else if (lastPage === page) {
              this.setAdditionalEfforts(res.data.content)
            } else if (lastPage === page - 1) {
              this.addAdditionalEfforts(res.data.content)
            } else {
              this.setAdditionalEfforts(res.data.content)
            }
            lastPage = page
          }
          return res
        })
    },
    /**
     * A page of the catalogue for a picker (`LewAutocompleteCatalog`), returned rather than written
     * into the store: the store's list is the additional effort overview's rows, and a picker's search hits replaced them.
     * Silent, as every picker's search is: a snackbar per keystroke helps nobody.
     */
    searchAdditionalEfforts({ search, page = 0, itemsPerPage }) {
      return apiClient
        .get("/additionaleffort", {
          params: { page, itemsPerPage, search: search || undefined },
          headers: { tenant: true, authorization: true },
          silent: true,
        })
        .then(res => ({ items: res.data?.content ?? [], hasMore: hasMoreRows(res.data, itemsPerPage) }))
    },
  },
})
